import axios, { AxiosResponse } from "axios";

import { AssetResponse } from "../FetchAssetDetails/types";

import { ATOM_HEADERS, LABELS_URL } from "@/constants/api";

/**
 * Formats a Tune-In badge using the provided date and template from the labels API.
 *
 * @param dateString - The date string in ISO format from the API.
 * @param template - The template string from the labels API for formatting the badge.
 * @returns The formatted Tune-In badge text in uppercase.
 *
 * @example
 * ```typescript
 * const badge = formatTuneInBadge("2023-10-15T00:00:00Z", "Watch %{MMMM} %{D}");
 * console.log(badge); // Outputs: "WATCH OCTOBER 15"
 * ```
 */
const formatTuneInBadge = (dateString: string, template: string): string => {
  const date = new Date(dateString);
  const result = template
    .replace("%{MMMM}", date.toLocaleString("en-US", { month: "long" }))
    .replace("%{D}", date.getDate().toString());
  return result.toUpperCase();
};

/**
 * Concatenate the numerical value representing the day of the week in the case of the "nextEpisodeDayWeek" badge
 *
 * @param template - Template string from labels API.
 *  @param dateString - Date string from API.
 * @returns template with day of the week concatenated at the end.
 *
 *  * @example
 * ```typescript
 * const template = concatDayOfWeek("tuneInBadging.NextEpisodeDayWeek", "2023-10-15T00:00:00Z");
 * console.log(template); // Outputs: "tuneInBadging.NextEpisodeDayWeek.1"
 * ```
 */
const concatDayOfWeek = (template: string, dateString: string): string => {
  const date = new Date(dateString);
  const dayOfWeek = date.getDay() + 1;
  template = template.concat(".", dayOfWeek.toString());
  return template;
};

/**
 * Processes a single asset to extract and format the Tune-In badge text.
 *
 * @param asset - The asset containing Tune-In badge metadata.
 * @param response - The Axios response object containing label templates.
 * @param forTile34 - Boolean representing whether the tune-in badge text will be for a 3:4 Tile.
 * @returns The formatted Tune-In badge text.
 */
const processBadge = (
  asset: AssetResponse,
  response: AxiosResponse,
  forTile34: boolean,
): string => {
  // Check if badging exists on this asset type
  if (!("badging" in asset.attributes)) {
    return "";
  }

  const tuneInBadging = asset.attributes.badging?.tuneInBadging;

  if (!tuneInBadging) return "";

  const dateString = tuneInBadging.value;
  let tuneInBadgingMessage = tuneInBadging.message;

  // Edge case for day-of-week badges
  if (tuneInBadgingMessage === "tuneInBadging.nextEpisodeDayWeek") {
    tuneInBadgingMessage = concatDayOfWeek(tuneInBadgingMessage, dateString);
  }

  const key = forTile34
    ? `watch.${tuneInBadgingMessage}.short`
    : `watch.${tuneInBadgingMessage}`;

  const template = response.data[key];

  // Return raw template if it doesn't contain placeholders, otherwise format it
  if (template && (template.includes("%{") || forTile34)) {
    return formatTuneInBadge(dateString, template);
  }

  return template || "";
};

/**
 * Fetches the formatted Tune-In badge text for a single asset or an array of assets from the API.
 *
 * @remarks
 * This asynchronous function retrieves the localized template for the Tune-In badge
 * from the labels API, formats the badge using the asset's metadata, and handles specific
 * badge types such as "nextEpisodeDayWeek" by appending the day of the week. If an error occurs,
 * it logs the error and returns an empty string.
 *
 * @param data - Either a single asset or an array of assets containing Tune-In badge metadata.
 * @param forTile34 - Boolean representing whether the tune-in badge text will be for a 3:4 Tile.
 * @returns A promise that resolves to an array of the formatted Tune-In badge texts for all assets in data.
 *
 * @example
 * ```typescript
 * const assetData = { attributes: { badging: { tuneInBadging: { value: "2023-10-15T00:00:00Z", message: "tuneInBadging.nextEpisodeDayWeek" } } } };
 * fetchTuneInBadge(assetData).then(badge => console.log(badge));
 * ```
 *
 * @throws Will log an error and return an empty string if the network request fails or the response is invalid.
 */
const fetchTuneInBadgeDetails = async (
  data: AssetResponse | AssetResponse[],
  forTile34: boolean,
): Promise<string[] | string> => {
  try {
    const response = await axios.get(LABELS_URL, {
      headers: ATOM_HEADERS,
    });

    if (Array.isArray(data)) {
      return data.map((asset) => processBadge(asset, response, forTile34));
    } else {
      return processBadge(data, response, forTile34);
    }
  } catch (error) {
    console.error("Error fetching tune-in badge:", error);
    return "";
  }
};

export { formatTuneInBadge, concatDayOfWeek, fetchTuneInBadgeDetails };
